import * as CookieConsent from 'vanilla-cookieconsent';
import 'vanilla-cookieconsent/dist/cookieconsent.css';
import { getConsentCookieDomain } from './cookie-domain';

export type ConsentCategory = 'necessary' | 'analytics' | 'marketing';
const ALL_CATEGORIES: ConsentCategory[] = ['necessary', 'analytics', 'marketing'];

export interface ConsentInitConfig {
  translations: Record<string, unknown>;
  hostname: string;
  /** Active locale (e.g. 'en', 'fr'); used as vanilla-cookieconsent's default language. */
  language: string;
}

const listeners: Array<(categories: ConsentCategory[]) => void> = [];

// vanilla-cookieconsent builds the preferences modal during run(); calling
// showPreferences() before that throws on the DOM refs it has not created yet, rather
// than no-oping. initConsent is awaited on page bootstrap, so any UI offering a "cookie
// preferences" affordance can be clicked before it resolves — hence the flag.
let consentInitialised = false;

function acceptedCategories(): ConsentCategory[] {
  return ALL_CATEGORIES.filter((c) => CookieConsent.acceptedCategory(c));
}

function notify(): void {
  const categories = acceptedCategories();
  listeners.forEach((cb) => cb(categories));
}

export type Unsubscribe = () => void;

export function onConsentChange(cb: (categories: ConsentCategory[]) => void): Unsubscribe {
  listeners.push(cb);
  return () => {
    const index = listeners.indexOf(cb);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  };
}

export function hasConsent(category: ConsentCategory): boolean {
  return CookieConsent.acceptedCategory(category);
}

/**
 * Whether the user has expressed a valid consent choice (a `cc_cookie` exists for
 * the current revision). Unlike `hasConsent('necessary')`, this is NOT true on a
 * fresh visit: read-only categories are auto-accepted on init, so use this to know
 * if the banner has actually been answered.
 */
export function hasGivenConsent(): boolean {
  return CookieConsent.validConsent();
}

/** Whether initConsent has completed, i.e. whether showPreferences can do anything. */
export function isConsentInitialised(): boolean {
  return consentInitialised;
}

/** Opens the preferences modal, or does nothing if consent has not been initialised yet. */
export function showPreferences(): void {
  if (!consentInitialised) {
    return;
  }
  CookieConsent.showPreferences();
}

export async function initConsent(config: ConsentInitConfig): Promise<void> {
  await CookieConsent.run({
    // Match Didomi: block page interaction with a dark overlay until the user
    // makes a choice (Accept / Reject / Manage are all available, so it stays a
    // forced choice, not an accept-only "cookie wall").
    disablePageInteraction: true,
    cookie: { name: 'cc_cookie', domain: getConsentCookieDomain(config.hostname), expiresAfterDays: 183 },
    guiOptions: {
      consentModal: { layout: 'box wide', position: 'middle center' },
      preferencesModal: { layout: 'box' },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      // autoClear deletes a category's cookies when the user opts out / withdraws.
      analytics: {
        autoClear: {
          cookies: [{ name: /^_ga/ }, { name: /^_hj/ }, { name: 'ajs_anonymous_id' }],
        },
      },
      marketing: {
        autoClear: {
          cookies: [
            { name: /^_gcl/ },
            { name: 'test_cookie' },
            { name: '_fbp' },
            { name: /^_uet/ },
            { name: 'MUID' },
            { name: /^prism_/ },
          ],
        },
      },
    },
    language: {
      default: config.language,
      translations: config.translations as Record<string, CookieConsent.Translation>,
    },
    // onConsent already fires on the first consent action and on every reload,
    // so we deliberately omit onFirstConsent to avoid a duplicate consent update.
    onConsent: notify,
    onChange: notify,
  });

  consentInitialised = true;
}
